/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useQaPaste.ts
 * FILE_ID: JOBHUB-QA-PASTE-HOOK-001
 * PURPOSE: Paste and duplicate-N mutations that refresh Build, Run, and ITP.
 */
import { useMutation, type QueryClient } from '@tanstack/react-query'
import type { QaApi, QaPasteBody } from '../../qa/qaApi'
import { qaErrorMessage } from './qaTabSelectors'
import { invalidateQaExecution } from './useQaWorkspace'

export function useQaPaste({
  api,
  queryClient,
  jobId,
  setError,
}: {
  api: QaApi
  queryClient: QueryClient
  jobId: number
  setError: (error: string | null) => void
}) {
  const refresh = () => {
    setError(null)
  }
  const settled = () => invalidateQaExecution(queryClient, jobId)
  const paste = useMutation({
    mutationFn: (body: QaPasteBody) => api.pasteQaNodes(body),
    onSuccess: refresh,
    onError: (error: unknown) => setError(qaErrorMessage(error, 'Could not paste that copy.')),
    onSettled: settled,
  })
  const duplicateMany = useMutation({
    mutationFn: ({ id, count }: { id: string; count: number }) => api.duplicateInspectionMany(id, count),
    onSuccess: refresh,
    onError: (error: unknown) => setError(qaErrorMessage(error, 'Could not duplicate that QA.')),
    onSettled: settled,
  })
  const pasteQa = useMutation({
    mutationFn: (sourceId: string) => api.duplicateInspection(sourceId, {
      job_id: String(jobId),
      ungrouped: true,
    }),
    onSuccess: refresh,
    onError: (error: unknown) => setError(qaErrorMessage(error, 'Could not paste that QA.')),
    onSettled: settled,
  })
  return { paste, duplicateMany, pasteQa }
}
